import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Chip
} from '@mui/material';
import { Check } from '@mui/icons-material';

const tiers = [
  {
    name: 'Basic',
    price: '$300',
    period: '/month',
    description: 'Perfect for early-stage startups',
    features: [
      'Security Assessment',
      'FinOps',
      'Guided Service Provisioning',
      'Email Support',
      '1 AWS Account'
    ],
    buttonText: 'Start Basic',
    buttonVariant: 'outlined'
  },
  {
    name: 'Standard',
    price: '$450',
    period: '/month',
    description: 'Ideal for growing companies',
    features: [
      'All Basic features',
      'Regulatory Compliance Scanning',
      'Infrastructure Consulting',
      'Observability & Alerting',
      'Priority Support',
      'Up to 5 AWS Accounts'
    ],
    buttonText: 'Start Standard',
    buttonVariant: 'contained',
    popular: true
  },
  {
    name: 'Premium',
    price: '$550',
    period: '/month',
    description: 'For mature enterprises',
    features: [
      'All Standard features',
      'AI-Powered Provisioning',
      'Automated Backup & DR',
      'Multi-Account Governance',
      'Dedicated Support',
      'Up to 10 AWS Accounts'
    ],
    buttonText: 'Start Premium',
    buttonVariant: 'outlined'
  }
];

function Pricing() {
  const navigate = useNavigate();

  const handleGetStarted = (tier) => {
    navigate('/register');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box textAlign="center" mb={6}>
        <Typography variant="h2" component="h1" gutterBottom>
          Choose Your Plan
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph>
          Self-hosted AWS automation platform with 14-day free trial
        </Typography>
      </Box>

      <Grid container spacing={4} alignItems="flex-end">
        {tiers.map((tier) => (
          <Grid item key={tier.name} xs={12} md={4}>
            <Card
              sx={{
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                ...(tier.popular && {
                  border: 2,
                  borderColor: 'primary.main'
                })
              }}
            >
              {tier.popular && (
                <Chip
                  label="Most Popular"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)'
                  }}
                />
              )}
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Typography variant="h4" component="h2" gutterBottom>
                  {tier.name}
                </Typography>
                <Box mb={2}>
                  <Typography variant="h3" component="span" color="primary">
                    {tier.price}
                  </Typography>
                  <Typography variant="h6" component="span" color="text.secondary">
                    {tier.period}
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" paragraph>
                  {tier.description}
                </Typography>
                <List>
                  {tier.features.map((feature) => (
                    <ListItem key={feature} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Check color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <CardActions sx={{ p: 2 }}>
                <Button
                  fullWidth
                  variant={tier.buttonVariant}
                  color="primary"
                  size="large"
                  onClick={() => handleGetStarted(tier.name.toLowerCase())}
                >
                  {tier.buttonText}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box mt={8} textAlign="center">
        <Typography variant="h4" gutterBottom>
          Deployment & Licensing
        </Typography>
        <Grid container spacing={4} mt={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Easy Installation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Download from GitHub and deploy with a single Docker Compose command. 
              Complete setup in under 5 minutes.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Secure & Private
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Self-hosted on your infrastructure with air-gapped support. 
              Your data never leaves your environment.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              14-Day Trial
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Full access to all features during the trial period. 
              No credit card required to get started.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

export default Pricing;